import React from 'react';
import { Box, Typography, useTheme, alpha } from '@mui/material';

const SectionTitle = ({ title, subtitle, align = 'left', color, diagonalLeft, gap = 0.5, sx = {} }) => {
    const theme = useTheme();
    const primaryColor = color || theme.palette.primary.main;

    const isCenter = align === 'center';

    const lineLeft = diagonalLeft || (isCenter ? '50%' : '14%');

    // Split title into two parts (first word and the rest)
    const words = title.split(' ');
    const firstWord = words[0];
    const restOfTitle = words.slice(1).join(' ');

    return (
        <Box
            sx={{
                position: 'relative',
                mb: 8,
                mt: 4,
                pt: 2,
                textAlign: align,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: isCenter ? 'center' : 'flex-start',
                ...sx
            }}
        >
            {/* Background Decorative Lines */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '40%',
                    left: isCenter ? '0' : '40px',
                    right: isCenter ? '0' : 'auto',
                    width: isCenter ? '100%' : '150px',
                    height: '1px',
                    background: `linear-gradient(90deg, ${alpha(primaryColor, 0.4)} 0%, ${alpha(primaryColor, 0)} 100%)`,
                    zIndex: 0,
                }}
            />

            <Box
                sx={{
                    position: 'absolute',
                    top: '-20px',
                    left: isCenter ? '50%' : '60px',
                    bottom: '0',
                    width: '1px',
                    background: `linear-gradient(180deg, ${alpha(primaryColor, 0)} 0%, ${alpha(primaryColor, 0.3)} 50%, ${alpha(primaryColor, 0)} 100%)`,
                    zIndex: 0,
                    transform: isCenter ? 'translateX(-50%)' : 'none',
                }}
            />

            {/* THE MAIN DIAGONAL LINE */}
            <Box
                sx={{
                    position: 'absolute',
                    top: '50%',
                    left: lineLeft,
                    width: { xs: '80px', sm: '120px' },
                    height: '2px',
                    bgcolor: primaryColor,
                    zIndex: 1,
                    transform: 'translate(-50%, -50%) rotate(-45deg)',
                    boxShadow: `0 0 10px ${alpha(primaryColor, 0.5)}`,
                    opacity: 0.8
                }}
            />

            {/* Text Container */}
            <Box
                sx={{
                    position: 'relative',
                    zIndex: 2,
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'baseline',
                    gap: gap,
                    pl: isCenter ? 0 : 4,
                }}
            >
                {/* First Word - Solid and High */}
                <Typography
                    variant="h3"
                    sx={{
                        fontFamily: 'Orbitron, sans-serif',
                        fontWeight: 900,
                        color: 'white',
                        fontSize: { xs: '1.4rem', sm: '2rem', md: '2.5rem' },
                        lineHeight: 1,
                        transform: 'translateY(-10px)',
                        textShadow: '2px 2px 4px rgba(0,0,0,0.5)'
                    }}
                >
                    {firstWord}
                </Typography>

                {/* Rest of Title - Outlined and Low */}
                {restOfTitle && (
                    <Typography
                        variant="h3"
                        sx={{
                            fontFamily: 'Orbitron, sans-serif',
                            fontWeight: 900,
                            color: 'transparent',
                            WebkitTextStroke: '1px rgba(255, 255, 255, 0.8)',
                            fontSize: { xs: '1.4rem', sm: '2rem', md: '2.5rem' },
                            lineHeight: 1,
                            transform: 'translateY(15px)',
                            letterSpacing: '-1px'
                        }}
                    >
                        {restOfTitle}
                    </Typography>
                )}
            </Box>

            {/* Optional Subtitle */}
            {subtitle && (
                <Typography
                    variant="caption"
                    sx={{
                        color: alpha('#fff', 0.5),
                        letterSpacing: '3px',
                        mt: 4,
                        pl: isCenter ? 0 : 5,
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        fontFamily: 'Orbitron, sans-serif',
                        fontSize: '0.65rem'
                    }}
                >
                    {subtitle}
                </Typography>
            )}
        </Box>
    );
};

export default SectionTitle;
